/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AssetType,
  AudioStore,
  BooleanHelper,
  IAppState,
  IMediaModel,
  MediaHelper,
  MediaType,
  RouteHelper,
  TimeHelper,
  useDispatch,
  useIsLoggedIn,
  useIsLoginToPlayRequired,
} from "@bms/common";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { LoginModal, MediaIcon } from "components";
import { useModal } from "context/modal";
import PlayFilledIcon from "resources/icons/play-filled.svg";
import CartIcon from "resources/icons/shopping-cart.svg";

export interface IPlayIconButtonProps {
  media?: IMediaModel;
  style?: React.CSSProperties;
  className?: string;
}

export const PlayIconButton = ({
  media,
  style,
  className,
}: IPlayIconButtonProps) => {
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const { setModal } = useModal();
  const { t } = useTranslation();
  const isLoginToPlayRequired = useIsLoginToPlayRequired();

  const isProductOwnedByUser = useSelector<IAppState, boolean>((state) =>
    MediaHelper.isUserOwnMedia(state.auth.user, media),
  );

  const onOpenLoginModal = useCallback(
    () => setModal({ content: <LoginModal /> }),
    [setModal],
  );

  const onPlayClick = useCallback(() => {
    if (media) {
      switch (media?.MediaTypeCode) {
        case AssetType.Podcast:
          dispatch(
            AudioStore.Actions.setAudioMedia({ media, isFromAutoPlay: true }),
          );
          break;
        default:
          RouteHelper.goToPlayer(media);
      }
    }
  }, [dispatch, media]);

  const onBuyClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      if (media) {
        RouteHelper.goToBuyAsset(media?.Id);
      }
    },
    [media],
  );

  if (
    !media ||
    media?.MediaTypeCode === MediaType.Article ||
    media?.MediaTypeCode === AssetType.Package ||
    media?.MediaTypeCode === MediaType.Advertisement
  ) {
    return null;
  }

  const ctaPlayFirstLogic = BooleanHelper.toBool(
    process.env.REACT_APP_CTA_PLAY_FIRST_LOGIC,
    false,
  );

  const currentDate = TimeHelper.getCurrentDate();
  const { StartDateTime, EndDateTime } = media;
  const isBeforeOrAfterAvailableDate =
    (StartDateTime && currentDate.isBefore(StartDateTime)) ||
    (EndDateTime && currentDate.isAfter(EndDateTime));
  const isAvailable = !isBeforeOrAfterAvailableDate;

  const playButton =
    media?.MediaTypeCode !== AssetType.Album ? (
      <MediaIcon
        style={style}
        className={className}
        ariaLabel={t(
          isLoginToPlayRequired
            ? "LOGIN_REQUIRED_ACTION"
            : "COMMON__BUTTON_PLAY",
        )}
        onIconClick={isLoginToPlayRequired ? onOpenLoginModal : onPlayClick}
        onBackgroundClick={() => RouteHelper.goToDetails(media)}
      >
        <PlayFilledIcon />
      </MediaIcon>
    ) : null;

  if (isProductOwnedByUser) {
    if (isAvailable && media.IsPlayable) {
      return playButton;
    }

    return null;
  } else {
    const buttonOrderIcon = ctaPlayFirstLogic ? (
      <PlayFilledIcon />
    ) : (
      <CartIcon />
    );
    const buttonOrderLabel = ctaPlayFirstLogic
      ? "COMMON__BUTTON_PLAY"
      : "COMMON__BUTTON_BUY_ASSET";

    return (
      <MediaIcon
        style={style}
        className={className}
        ariaLabel={t(buttonOrderLabel)}
        onIconClick={isLoggedIn ? onBuyClick : onOpenLoginModal}
        onBackgroundClick={() => RouteHelper.goToDetails(media)}
      >
        {buttonOrderIcon}
      </MediaIcon>
    );
  }
};
